import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import FacebookPagesFilter from '@/components/ChannelsFilter/FacebookPagesFilter';
import { Controls, ControlsButton } from '@/components/Controls';
import FiltersDropdown from '@/components/FilterDropdown';
import FilterButton from '@/components/FilterDropdown/FilterButton';
import FilterRadioButton from '@/components/FilterDropdown/FilterRadioButton';
import {
  FilterGroupBody,
  FilterGroupHeader,
  FilterGroupTitle,
  FilterWrap,
} from '@/components/FilterDropdown/Parts';
import { PageHeading, TeamHeading } from '@/components/Layout/Parts';
import { DistributionDepartmentsEnum, ReleaseStatusEnum } from '@/utils/enums';

import DistributionCalendar from './components/DistributionCalendar';
import DistributionList from './components/DistributionList';
import DistributionPreparedDraftList from './components/DistributionPreparedDraftList';
import DistributionScheduleModal from './components/DistributionScheduleModal';

enum ViewTabs {
  List,
  Month,
}

export enum PeriodTabs {
  FromToday,
  UntilToday,
}

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledChannelsFilter = styled(FacebookPagesFilter)`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div<{ $pushRight?: boolean }>`
  display: flex;
  align-items: flex-end;
  margin: 0 0 12px;
  ${({ $pushRight }) =>
    $pushRight &&
    css`
      justify-content: flex-end;
    `}
`;

const ViewControls = styled(Controls)`
  margin-right: auto;
`;

const FiltersHolder = styled.div`
  display: flex;
  gap: 8px;
`;

interface Props {
  status?: ReleaseStatusEnum;
  withRepostsOnly?: boolean;
}

const DistributionSchedule: FC<Props> = ({
  status,
  withRepostsOnly = false,
}) => {
  const [selectedChannels, setSelectedChannels] = useState<number[]>([]);
  const [activeViewTab, setActiveViewTab] = useState(ViewTabs.List);
  const [activePeriodTab, setActivePeriodTab] = useState(PeriodTabs.FromToday);
  const [activeStatus, setActiveStatus] = useState<ReleaseStatusEnum | null>(
    null,
  );
  const [activeDepartment, setActiveDepartment] =
    useState<DistributionDepartmentsEnum>(DistributionDepartmentsEnum.ALL);
  const [activeType, setActiveType] = useState<null | 0 | 1>(null);
  const [activeReserved, setActiveReserved] = useState<null | 0 | 1>(null);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showReservedFilter, setShowReservedFilter] = useState(false);

  const [showDepartmentFilter, setShowDepartmentFilter] = useState(false);
  const [showTypeFilter, setShowTypeFilter] = useState(false);

  const renderPageHeading = () => {
    if (withRepostsOnly) {
      return 'Conditional Reposts';
    }

    switch (status) {
      case ReleaseStatusEnum.PREPARED:
        return 'Prepared';
      case ReleaseStatusEnum.DRAFT:
        return 'Draft';
      default:
        return 'Distribution';
    }
  };

  return (
    <>
      <TeamHeading />
      <HeadingRow>
        <PageHeading>{renderPageHeading()}</PageHeading>
        <Button onClick={() => setShowScheduleModal((prev) => !prev)}>
          New schedule
        </Button>
      </HeadingRow>
      <SectionHeader $pushRight={!!status}>
        {!status && (
          <>
            <ViewControls>
              <ControlsButton
                active={activeViewTab === ViewTabs.List}
                onClick={() => setActiveViewTab(ViewTabs.List)}
              >
                List
              </ControlsButton>
              <ControlsButton
                active={activeViewTab === ViewTabs.Month}
                onClick={() => setActiveViewTab(ViewTabs.Month)}
              >
                Month
              </ControlsButton>
            </ViewControls>
          </>
        )}
        <FiltersHolder>
          {activeViewTab === ViewTabs.List && (
            <Controls>
              <ControlsButton
                active={activePeriodTab === PeriodTabs.FromToday}
                onClick={() => setActivePeriodTab(PeriodTabs.FromToday)}
              >
                From Today
              </ControlsButton>
              <ControlsButton
                active={activePeriodTab === PeriodTabs.UntilToday}
                onClick={() => setActivePeriodTab(PeriodTabs.UntilToday)}
              >
                Until Today
              </ControlsButton>
            </Controls>
          )}
          <FilterWrap>
            <FilterButton
              isActive={showDepartmentFilter}
              text="Department"
              onClick={(e) => {
                e.stopPropagation();
                setShowDepartmentFilter((prev) => !prev);
              }}
              filterApplied={
                activeDepartment !== DistributionDepartmentsEnum.ALL
              }
            />
            {showDepartmentFilter && (
              <FiltersDropdown onClose={() => setShowDepartmentFilter(false)}>
                <FilterGroupHeader>
                  <FilterGroupTitle>Department</FilterGroupTitle>
                </FilterGroupHeader>
                <FilterGroupBody>
                  <FilterRadioButton
                    id="departmentAll"
                    value={DistributionDepartmentsEnum.ALL.toString()}
                    checked={
                      activeDepartment === DistributionDepartmentsEnum.ALL
                    }
                    setValue={() =>
                      setActiveDepartment(DistributionDepartmentsEnum.ALL)
                    }
                  >
                    All
                  </FilterRadioButton>
                  <FilterRadioButton
                    id="departmentNative"
                    value={DistributionDepartmentsEnum.NATIVE.toString()}
                    checked={
                      activeDepartment === DistributionDepartmentsEnum.NATIVE
                    }
                    setValue={() =>
                      setActiveDepartment(DistributionDepartmentsEnum.NATIVE)
                    }
                  >
                    Native
                  </FilterRadioButton>
                  <FilterRadioButton
                    id="department3rdPt"
                    value={DistributionDepartmentsEnum.THIRD_PT.toString()}
                    checked={
                      activeDepartment === DistributionDepartmentsEnum.THIRD_PT
                    }
                    setValue={() =>
                      setActiveDepartment(DistributionDepartmentsEnum.THIRD_PT)
                    }
                  >
                    3rd Party
                  </FilterRadioButton>
                </FilterGroupBody>
              </FiltersDropdown>
            )}
          </FilterWrap>
          <FilterWrap>
            <FilterButton
              isActive={showTypeFilter}
              text="Type"
              onClick={(e) => {
                e.stopPropagation();
                setShowTypeFilter((prev) => !prev);
              }}
              filterApplied={activeType !== null}
            />
            {showTypeFilter && (
              <FiltersDropdown onClose={() => setShowTypeFilter(false)}>
                <FilterGroupHeader>
                  <FilterGroupTitle>By Type:</FilterGroupTitle>
                </FilterGroupHeader>
                <FilterGroupBody>
                  <FilterRadioButton
                    id="typeAll"
                    value={''}
                    checked={activeType === null}
                    setValue={() => setActiveType(null)}
                  >
                    All
                  </FilterRadioButton>
                  <FilterRadioButton
                    id="typeVideo"
                    value={'0'}
                    checked={activeType === 0}
                    setValue={() => setActiveType(0)}
                  >
                    Video
                  </FilterRadioButton>
                  <FilterRadioButton
                    id="typeReels"
                    value={'1'}
                    checked={activeType === 1}
                    setValue={() => setActiveType(1)}
                  >
                    Reels
                  </FilterRadioButton>
                </FilterGroupBody>
              </FiltersDropdown>
            )}
          </FilterWrap>
          {!status && (
            <FilterWrap>
              <FilterButton
                text="Status"
                isActive={showFilters}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFilters((prev) => !prev);
                }}
                filterApplied={!!activeStatus}
              />
              {showFilters && (
                <FiltersDropdown onClose={() => setShowFilters(false)}>
                  <FilterGroupHeader>
                    <FilterGroupTitle>Status</FilterGroupTitle>
                  </FilterGroupHeader>
                  <FilterGroupBody>
                    <FilterRadioButton
                      id={`statusRadioAll`}
                      value=""
                      checked={!activeStatus}
                      setValue={() => setActiveStatus(null)}
                    >
                      All
                    </FilterRadioButton>
                    <FilterRadioButton
                      id={`statusRadio${ReleaseStatusEnum.WAITING}}`}
                      value={ReleaseStatusEnum.WAITING.toString()}
                      checked={activeStatus === ReleaseStatusEnum.WAITING}
                      setValue={() =>
                        setActiveStatus(ReleaseStatusEnum.WAITING)
                      }
                    >
                      Waiting
                    </FilterRadioButton>
                    <FilterRadioButton
                      id={`statusRadio${ReleaseStatusEnum.SCHEDULED}}`}
                      value={ReleaseStatusEnum.SCHEDULED.toString()}
                      checked={activeStatus === ReleaseStatusEnum.SCHEDULED}
                      setValue={() =>
                        setActiveStatus(ReleaseStatusEnum.SCHEDULED)
                      }
                    >
                      Scheduled
                    </FilterRadioButton>
                    <FilterRadioButton
                      id={`statusRadio${ReleaseStatusEnum.POSTED}}`}
                      value={ReleaseStatusEnum.POSTED.toString()}
                      checked={activeStatus === ReleaseStatusEnum.POSTED}
                      setValue={() => setActiveStatus(ReleaseStatusEnum.POSTED)}
                    >
                      Posted
                    </FilterRadioButton>
                    <FilterRadioButton
                      id={`statusRadio${ReleaseStatusEnum.REJECTED}}`}
                      value={ReleaseStatusEnum.REJECTED.toString()}
                      checked={activeStatus === ReleaseStatusEnum.REJECTED}
                      setValue={() =>
                        setActiveStatus(ReleaseStatusEnum.REJECTED)
                      }
                    >
                      Rejected
                    </FilterRadioButton>
                  </FilterGroupBody>
                </FiltersDropdown>
              )}
            </FilterWrap>
          )}
          <FilterWrap>
            <FilterButton
              isActive={showReservedFilter}
              text="Reserve"
              onClick={(e) => {
                e.stopPropagation();
                setShowReservedFilter((prev) => !prev);
              }}
              filterApplied={activeReserved !== null}
            />
            {showReservedFilter && (
              <FiltersDropdown onClose={() => setShowReservedFilter(false)}>
                <FilterGroupHeader>
                  <FilterGroupTitle>Reserve</FilterGroupTitle>
                </FilterGroupHeader>
                <FilterGroupBody>
                  <FilterRadioButton
                    id="reservedAll"
                    value={''}
                    checked={activeReserved === null}
                    setValue={() => setActiveReserved(null)}
                  >
                    All
                  </FilterRadioButton>
                  <FilterRadioButton
                    id="reservedReserved"
                    value={'1'}
                    checked={activeReserved === 1}
                    setValue={() => setActiveReserved(1)}
                  >
                    With Reserve
                  </FilterRadioButton>
                  <FilterRadioButton
                    id="reservedNotReserved"
                    value={'0'}
                    checked={activeReserved === 0}
                    setValue={() => setActiveReserved(0)}
                  >
                    Without Reserve
                  </FilterRadioButton>
                </FilterGroupBody>
              </FiltersDropdown>
            )}
          </FilterWrap>
        </FiltersHolder>
      </SectionHeader>
      <StyledChannelsFilter setChannels={setSelectedChannels} />
      {!status ? (
        <>
          {activeViewTab === ViewTabs.List && (
            <DistributionList
              withRepostsOnly={withRepostsOnly}
              activeTab={activePeriodTab}
              activeStatus={activeStatus}
              activeDepartment={activeDepartment}
              selectedChannels={selectedChannels}
              activeType={activeType}
              activeReserved={activeReserved}
            />
          )}
          {activeViewTab === ViewTabs.Month && (
            <DistributionCalendar
              selectedChannels={selectedChannels}
              activeStatus={activeStatus}
              activeDepartment={activeDepartment}
              activeType={activeType}
              activeReserved={activeReserved}
            />
          )}
        </>
      ) : (
        <DistributionPreparedDraftList
          selectedChannels={selectedChannels}
          status={status}
          activeDepartment={activeDepartment}
          activeType={activeType}
          activeReserved={activeReserved}
        />
      )}
      <AnimatePresence>
        {showScheduleModal && (
          <DistributionScheduleModal
            onClose={() => setShowScheduleModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default DistributionSchedule;
